import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import henceforthApi from '../../utils/henceforthApi';
import { GlobalContext, handleError } from '../../context/Provider';
import { subLists, VariantData, MultipleSelect } from './productInterface';
import { productValidationSchema, ProductFormValues } from './productValidation';
import SectionCard from './components/SectionCard';
import FormField from './components/FormField';
import SelectField from './components/SelectField';
import ImageUploader from './components/ImageUploader';
import Spinner from '../../components/common/spinner';
import {
  Save,
  X,
  Plus,
  Trash2,
  Shield,
  Tag,
  DollarSign,
  Layers,
  Sparkles,
  Info,
  CheckCircle2,
  AlertCircle,
  Copy,
} from 'lucide-react';
import commonArray from '../../components/common/commonArray';
import { Button } from '../../components/ui/button';

export interface ProductFormProps {
  mode: 'add' | 'edit' | 'clone';
  productId?: string;
  initialValues?: any;
  onSubmitSuccess?: (response: any) => void;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  mode,
  productId,
  initialValues,
  onSubmitSuccess,
}) => {
  const { language_data, authState, authDispatch, toastMessage } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(mode !== 'add' && !initialValues);

  // Cascading Category & Brand data
  const [brands, setBrands] = useState<subLists[]>([]);
  const [categories, setCategories] = useState<subLists[]>([]);
  const [subcategories, setSubcategories] = useState<subLists[]>([]);
  const [subSubcategories, setSubSubcategories] = useState<subLists[]>([]);

  // Dropdown loading states
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState<boolean>(false);
  const [loadingSubSubCategories, setLoadingSubSubCategories] = useState<boolean>(false);

  // Images state
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string>('');

  // Variant state
  const [variant, setVariant] = useState<VariantData>({
    variant_type: '',
    color: '#1AA7DE',
    color_name: '',
    label: '',
    value: '',
  });

  // Highlights state
  const [highlightsArray, setHighlightsArray] = useState<string[]>([]);
  const [currentHighlight, setCurrentHighlight] = useState<string>('');
  const [highlightError, setHighlightError] = useState<string>('');

  // Services state
  const [servicesArray, setServicesArray] = useState<string[]>([]);
  const [currentService, setCurrentService] = useState<string>('');
  const [serviceError, setServiceError] = useState<string>('');

  // Specifications state
  const [specificationsArray, setSpecificationsArray] = useState<MultipleSelect[]>([]);
  const [specKey, setSpecKey] = useState<string>('');
  const [specValue, setSpecValue] = useState<string>('');
  const [specError, setSpecError] = useState<string>('');

  // React Hook Form
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productValidationSchema),
    defaultValues: {
      name: '',
      description: '',
      price: '',
      tax_percentage: '',
      quantity: '',
      discount_percantage: '',
      brand_id: '',
      category_id: '',
      subcategory_id: '',
      sub_subcategory_id: '',
      status: true,
      is_featured: false,
    },
  });

  const selectedCategoryId = watch('category_id');
  const selectedSubcategoryId = watch('subcategory_id');
  const productNameValue = watch('name');

  // Track unsaved changes for browser unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty || newImageFiles.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, newImageFiles.length]);

  // Fetch initial Brands & Categories
  useEffect(() => {
    const fetchBrandsAndCategories = async () => {
      try {
        setLoadingBrands(true);
        setLoadingCategories(true);
        const [brandRes, catRes] = await Promise.all([
          henceforthApi.categorylisting.Brandlisting(),
          henceforthApi.categorylisting.categorylisting(),
        ]);
        setBrands(brandRes.data?.data || brandRes.data || []);
        setCategories(catRes.data?.data || catRes.data || []);
      } catch (err) {
        handleError(err, '', authDispatch);
      } finally {
        setLoadingBrands(false);
        setLoadingCategories(false);
      }
    };

    fetchBrandsAndCategories();
  }, [authDispatch]);

  // Fetch Subcategories
  const fetchSubcategories = useCallback(async (catId: string) => {
    if (!catId) {
      setSubcategories([]);
      return;
    }
    try {
      setLoadingSubCategories(true);
      const res = await henceforthApi.categorylisting.subCategorylisting(catId);
      setSubcategories(res.data?.data || res.data || []);
    } catch (err) {
      handleError(err, '', authDispatch);
    } finally {
      setLoadingSubCategories(false);
    }
  }, [authDispatch]);

  // Fetch Sub-Subcategories
  const fetchSubSubcategories = useCallback(async (subCatId: string) => {
    if (!subCatId) {
      setSubSubcategories([]);
      return;
    }
    try {
      setLoadingSubSubCategories(true);
      const res = await henceforthApi.categorylisting.subSubCategorylisting(subCatId);
      setSubSubcategories(res.data?.data || res.data || []);
    } catch (err) {
      handleError(err, '', authDispatch);
    } finally {
      setLoadingSubSubCategories(false);
    }
  }, [authDispatch]);

  // Populate data in Edit and Clone modes
  useEffect(() => {
    if ((mode === 'edit' || mode === 'clone') && productId) {
      const fetchProductDetails = async () => {
        try {
          setPageLoading(true);
          const res = await henceforthApi.Product.getProductDetails(productId);
          const data = res.data?.data || res.data;

          if (data) {
            reset({
              name: data.name || '',
              description: data.description || '',
              price: String(data.price ?? ''),
              tax_percentage: String(data.tax_percentage ?? ''),
              quantity: String(data.quantity ?? ''),
              discount_percantage: String(data.discount_percantage ?? ''),
              brand_id: data.brand_id?._id || data.brand_id || '',
              category_id: data.category_id?._id || data.category_id || '',
              subcategory_id: data.subcategory_id?._id || data.subcategory_id || '',
              sub_subcategory_id: data.sub_subcategory_id?._id || data.sub_subcategory_id || '',
              status: data.is_blocked === false,
              is_featured: !!data.is_featured,
            });

            if (data.category_id?._id || data.category_id) {
              fetchSubcategories(data.category_id?._id || data.category_id);
            }
            if (data.subcategory_id?._id || data.subcategory_id) {
              fetchSubSubcategories(data.subcategory_id?._id || data.subcategory_id);
            }

            if (Array.isArray(data.images)) {
              setExistingImages(data.images);
            }

            if (data.variant_type && data.variant_value) {
              const vColor =
                data.variant_value.color ||
                (data.variant_type === 'COLOR' ? data.variant_value.value : '#1AA7DE') ||
                '#1AA7DE';
              const vColorName =
                data.variant_value.color_name ||
                (data.variant_type === 'COLOR' ? data.variant_value.label : '') ||
                '';
              const vLabel = data.variant_value.label || (data.variant_type === 'COLOR' ? vColorName : '');
              const vValue = data.variant_value.value || (data.variant_type === 'COLOR' ? vColor : '');

              setVariant({
                variant_type: data.variant_type,
                color: vColor,
                color_name: vColorName,
                label: vLabel,
                value: vValue,
              });
            }

            if (Array.isArray(data.highlights)) {
              setHighlightsArray(data.highlights);
            } else if (Array.isArray(data.product_highlights)) {
              setHighlightsArray(
                data.product_highlights
                  .map((h: any) => h?.content || h)
                  .filter((h: any) => typeof h === 'string' && h.trim())
              );
            }

            if (Array.isArray(data.specifications)) {
              setSpecificationsArray(data.specifications);
            } else if (Array.isArray(data.productdetails)) {
              setSpecificationsArray(
                data.productdetails
                  .map((s: any) => ({ key: s?.key || '', value: s?.value || '' }))
                  .filter((s: any) => s.key && s.value)
              );
            }

            if (Array.isArray(data.services)) {
              setServicesArray(data.services);
            } else if (Array.isArray(data.product_services)) {
              setServicesArray(
                data.product_services
                  .map((s: any) => s?.content || s)
                  .filter((s: any) => typeof s === 'string' && s.trim())
              );
            }
          }
        } catch (err) {
          handleError(err, '', authDispatch);
        } finally {
          setPageLoading(false);
        }
      };

      fetchProductDetails();
    }
  }, [mode, productId, reset, fetchSubcategories, fetchSubSubcategories, authDispatch]);

  // Handle Category Change
  const handleCategoryChange = (catId: string) => {
    setValue('category_id', catId, { shouldValidate: true, shouldDirty: true });
    setValue('subcategory_id', '', { shouldValidate: true, shouldDirty: true });
    setValue('sub_subcategory_id', '', { shouldValidate: false, shouldDirty: true });
    setSubcategories([]);
    setSubSubcategories([]);
    if (catId) {
      fetchSubcategories(catId);
    }
  };

  // Handle SubCategory Change
  const handleSubCategoryChange = (subCatId: string) => {
    setValue('subcategory_id', subCatId, { shouldValidate: true, shouldDirty: true });
    setValue('sub_subcategory_id', '', { shouldValidate: false, shouldDirty: true });
    setSubSubcategories([]);
    if (subCatId) {
      fetchSubSubcategories(subCatId);
    }
  };

  // Highlights handlers
  const handleAddHighlight = () => {
    const trimmed = currentHighlight.trim();
    if (!trimmed) {
      setHighlightError('Highlight cannot be empty');
      return;
    }
    if (highlightsArray.includes(trimmed)) {
      setHighlightError('This highlight has already been added');
      return;
    }
    setHighlightsArray([...highlightsArray, trimmed]);
    setCurrentHighlight('');
    setHighlightError('');
  };

  const handleRemoveHighlight = (index: number) => {
    setHighlightsArray(highlightsArray.filter((_, i) => i !== index));
  };

  // Services handlers
  const handleAddService = () => {
    const trimmed = currentService.trim();
    if (!trimmed) {
      setServiceError('Service description cannot be empty');
      return;
    }
    if (servicesArray.includes(trimmed)) {
      setServiceError('This service has already been added');
      return;
    }
    setServicesArray([...servicesArray, trimmed]);
    setCurrentService('');
    setServiceError('');
  };

  const handleRemoveService = (index: number) => {
    setServicesArray(servicesArray.filter((_, i) => i !== index));
  };

  // Specifications handlers
  const handleAddSpecification = () => {
    const trimmedKey = specKey.trim();
    const trimmedVal = specValue.trim();
    if (!trimmedKey || !trimmedVal) {
      setSpecError('Both key and value are required');
      return;
    }
    if (specificationsArray.some((s) => s.key.toLowerCase() === trimmedKey.toLowerCase())) {
      setSpecError(`Specification key "${trimmedKey}" already exists`);
      return;
    }
    setSpecificationsArray([...specificationsArray, { key: trimmedKey, value: trimmedVal }]);
    setSpecKey('');
    setSpecValue('');
    setSpecError('');
  };

  const handleRemoveSpecification = (index: number) => {
    setSpecificationsArray(specificationsArray.filter((_, i) => i !== index));
  };

  // Validate images
  const validateImages = (): boolean => {
    if (existingImages.length === 0 && newImageFiles.length === 0) {
      setImageError('At least 1 product image is required');
      const elem = document.getElementById('product-images-card');
      elem?.scrollIntoView({ behavior: 'smooth' });
      return false;
    }
    setImageError('');
    return true;
  };

  // Cancel handler
  const handleCancel = () => {
    if (isDirty || newImageFiles.length > 0) {
      if (window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
        navigate('/products/1');
      }
    } else {
      navigate('/products/1');
    }
  };

  // Form Submit Handler
  const onFormSubmit = async (values: ProductFormValues) => {
    if (!validateImages()) return;

    try {
      setIsSubmitting(true);

      let uploadedImageNames: string[] = [];
      if (newImageFiles.length > 0) {
        const uploadPromises = newImageFiles.map(async (file) => {
          const apiRes = await henceforthApi.Common.do_spaces_file_upload('file', file);
          return apiRes.data?.file_name || apiRes.data?.name || apiRes.data;
        });
        uploadedImageNames = await Promise.all(uploadPromises);
      }

      const allImages = [...existingImages, ...uploadedImageNames];

      let variant_value: any = { label: '', value: '' };
      if (variant.variant_type === 'COLOR') {
        const colorVal = variant.color || variant.value || '#000000';
        const colorName = variant.color_name || variant.label || 'Color';
        variant_value = {
          label: colorName,
          value: colorVal,
          color: colorVal,
          color_name: colorName,
        };
      } else if (variant.variant_type === 'SIZE') {
        variant_value = {
          label: variant.label || 'Size',
          value: variant.value || '',
        };
      } else if (variant.variant_type) {
        variant_value = {
          label: variant.label || '',
          value: variant.value || '',
        };
      }

      const variantData = {
        variant_type: variant.variant_type || '',
        variant_value: variant.variant_type ? variant_value : { label: '', value: '' },
      };

      if (mode === 'add' || mode === 'clone') {
        const items: any = {
          name: values.name,
          description: values.description,
          brand_id: values.brand_id,
          category_id: values.category_id,
          subcategory_id: values.subcategory_id,
          images: allImages,
          price: Number(values.price),
          quantity: Number(values.quantity),
          tax_percentage: Number(values.tax_percentage),
          discount_percantage: Number(values.discount_percantage || 0),
          services: servicesArray,
          highlights: highlightsArray,
          language: authState.lang || 'ENGLISH',
          ...variantData,
        };

        if (values.sub_subcategory_id) {
          items.sub_subcategory_id = values.sub_subcategory_id;
        }

        if (specificationsArray.length) {
          items.specifications = specificationsArray;
          items.product_details = specificationsArray;
        }

        if (mode === 'clone' && productId) {
          items.clone_product_id = productId;
        }

        const apiRes = await henceforthApi.Product.add(items);
        const newProductId = apiRes.data?._id || apiRes.data;

        if (mode === 'clone' && productId && newProductId) {
          try {
            const variantsItems = {
              product_id_1: productId,
              product_id_2: newProductId,
              language: 'ENGLISH',
            };
            await henceforthApi.Product.addProductDetailsDynamically(
              commonArray.productOtherDetails[2],
              variantsItems
            );
          } catch (varErr) {
            console.error('Failed to link cloned variant:', varErr);
          }
        }

        toastMessage(
          apiRes.message ||
          (mode === 'clone' ? 'Product cloned successfully' : 'Product created successfully')
        );

        if (onSubmitSuccess) {
          onSubmitSuccess(apiRes);
        } else {
          navigate(`/product/${newProductId}`);
        }
      } else {
        const items = {
          _id: productId,
          name: values.name,
          description: values.description,
          brand_id: values.brand_id,
          category_id: values.category_id,
          subcategory_id: values.subcategory_id,
          sub_subcategory_id: values.sub_subcategory_id || '',
          images: allImages,
          tax_percentage: Number(values.tax_percentage),
          quantity: Number(values.quantity),
          price: Number(values.price),
          discount_percantage: Number(values.discount_percantage || 0),
          language: 'ENGLISH',
          ...variantData,
        };

        const apiRes = await henceforthApi.Product.editProduct(items);
        toastMessage(apiRes.message || 'Product updated successfully');
        if (onSubmitSuccess) {
          onSubmitSuccess(apiRes);
        } else {
          navigate(-1);
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      handleError(err, 'active', authDispatch);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-[60vh]">
        <Spinner color="text-[#1AA7DE]" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-4 space-y-4 bg-slate-50/50 min-h-screen">
      <form onSubmit={handleSubmit(onFormSubmit)} noValidate>

        {/* Top Hero / Action Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-4 py-3.5 rounded-xl border border-slate-100 shadow-xs mb-4">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight m-0">
                  {mode === 'add'
                    ? 'Add New Product'
                    : mode === 'clone'
                      ? 'Clone Product'
                      : 'Edit Product'}
                </h1>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-sky-50 text-[#1AA7DE] border border-sky-200/60 shadow-2xs">
                  {mode === 'add'
                    ? 'New Listing'
                    : mode === 'clone'
                      ? `Cloning #${productId?.slice(-8) || ''}`
                      : `#${productId?.slice(-8) || ''}`}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium mt-0.5 mb-0">
                {mode === 'add'
                  ? 'Fill in the information below to add this item to your store catalog.'
                  : mode === 'clone'
                    ? 'Create a new product variation pre-populated with details from the cloned product.'
                    : 'Update catalog specifications, inventory stock, media assets, and pricing.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto md:ml-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
            </Button>

            <Button
              type="submit"
              variant="default"
              size="sm"
              disabled={isSubmitting}
              className="gap-1.5"
            >
              {isSubmitting ? (
                <Spinner color="text-white" />
              ) : (
                <>
                  {mode === 'clone' ? (
                    <Copy className="w-4 h-4" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>
                    {mode === 'add'
                      ? language_data?.[authState.lang]?.common_save || 'Save Product'
                      : mode === 'clone'
                        ? language_data?.[authState.lang]?.common_clone || 'Clone Product'
                        : language_data?.[authState.lang]?.common_edit || 'Update Product'}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* 2-Column Responsive Form Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">

          {/* LEFT COLUMN: Main Product Details (8 cols) */}
          <div className="lg:col-span-8 space-y-4">

            {/* 1. Basic Information */}
            <SectionCard
              id="basic-info-card"
              title={`${language_data?.[authState.lang]?.common_product || 'Product'} Information`}
              subtitle="Enter the title and full description for this item"
              icon={<Info className="w-5 h-5" />}
            >
              <FormField
                label={`${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_name || 'Name'
                  }`}
                required
                maxLength={95}
                showCharCount
                placeholder="e.g. Premium Cotton Crewneck T-Shirt"
                error={errors.name?.message}
                {...register('name')}
                value={productNameValue || ''}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <FormField
                    isTextarea
                    rows={5}
                    label={`${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_description || 'Description'
                      }`}
                    required
                    placeholder="Provide a detailed, compelling product overview highlighting key benefits, materials, and sizing..."
                    error={errors.description?.message}
                    {...field}
                  />
                )}
              />
            </SectionCard>

            {/* 2. Pricing & Stock Inventory */}
            <SectionCard
              id="pricing-inventory-card"
              title="Pricing & Inventory"
              subtitle="Configure price points, applicable taxes, and available stock units"
              icon={<DollarSign className="w-5 h-5" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <FormField
                      type="number"
                      step="0.01"
                      min="0"
                      label={`${language_data?.[authState.lang]?.common_product || 'Product'} Price`}
                      required
                      prefix="$"
                      placeholder="0.00"
                      error={errors.price?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="tax_percentage"
                  control={control}
                  render={({ field }) => (
                    <FormField
                      type="number"
                      min="0"
                      max="100"
                      label="Tax Rate"
                      required
                      suffix="%"
                      placeholder="0"
                      error={errors.tax_percentage?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="quantity"
                  control={control}
                  render={({ field }) => (
                    <FormField
                      type="number"
                      min="0"
                      label="Inventory Stock Quantity"
                      required
                      suffix="units"
                      placeholder="0"
                      error={errors.quantity?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="discount_percantage"
                  control={control}
                  render={({ field }) => (
                    <FormField
                      type="number"
                      min="0"
                      max="100"
                      label="Discount (Optional)"
                      suffix="%"
                      placeholder="0"
                      error={errors.discount_percantage?.message}
                      {...field}
                    />
                  )}
                />
              </div>
            </SectionCard>

            {/* 3. Product Media Assets */}
            <SectionCard
              id="product-images-card"
              title="Product Media & Gallery"
              subtitle="Upload high quality product photography to maximize buyer conversions"
              icon={<Layers className="w-5 h-5" />}
            >
              <ImageUploader
                existingImages={existingImages}
                newFiles={newImageFiles}
                error={imageError}
                onChange={({ existingImages: exp, newFiles: nfl }) => {
                  setExistingImages(exp);
                  setNewImageFiles(nfl);
                  if (exp.length > 0 || nfl.length > 0) {
                    setImageError('');
                  }
                }}
              />
            </SectionCard>

            {/* 4. Product Variants (Optional) */}
            <SectionCard
              id="variant-card"
              title={language_data?.[authState.lang]?.common_variant || 'Product Variant (Optional)'}
              subtitle="Select if this item comes in specific color or size configurations"
              icon={<Sparkles className="w-5 h-5" />}
            >
              <div className="space-y-4">
                {/* Segmented Variant Selector */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide mb-2">
                    Variant Configuration
                  </label>
                  <div className="inline-flex items-center p-1 bg-slate-100 rounded-xl text-xs font-bold text-slate-600 gap-1">
                    <button
                      type="button"
                      onClick={() =>
                        setVariant({
                          variant_type: '',
                          color: '#1AA7DE',
                          color_name: '',
                          label: '',
                          value: '',
                        })
                      }
                      className={`px-4 py-2 rounded-lg transition-all ${variant.variant_type === ''
                        ? 'bg-white text-slate-900 shadow-2xs'
                        : 'hover:text-slate-900 text-slate-600'
                        }`}
                    >
                      None (Single Product)
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setVariant((prev) => {
                          const col = prev.color || '#1AA7DE';
                          const name = prev.color_name || prev.label || 'Sky Blue';
                          return {
                            variant_type: 'COLOR',
                            color: col,
                            color_name: name,
                            label: name,
                            value: col,
                          };
                        })
                      }
                      className={`px-4 py-2 rounded-lg transition-all ${variant.variant_type === 'COLOR'
                        ? 'bg-white text-[#1AA7DE] shadow-2xs'
                        : 'hover:text-slate-900 text-slate-600'
                        }`}
                    >
                      Color Variant
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setVariant((prev) => {
                          const lbl = prev.label || 'Size';
                          const val = prev.value || 'Medium';
                          return {
                            variant_type: 'SIZE',
                            color: prev.color || '#1AA7DE',
                            color_name: '',
                            label: lbl,
                            value: val,
                          };
                        })
                      }
                      className={`px-4 py-2 rounded-lg transition-all ${variant.variant_type === 'SIZE'
                        ? 'bg-white text-[#1AA7DE] shadow-2xs'
                        : 'hover:text-slate-900 text-slate-600'
                        }`}
                    >
                      Size Variant
                    </button>
                  </div>
                </div>

                {/* Color Configuration */}
                {variant.variant_type === 'COLOR' && (
                  <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs sm:text-sm font-bold text-slate-700 tracking-wide m-0">
                          Color Swatch
                        </label>
                      </div>
                      <div className="flex items-center gap-2.5 h-[38px]">
                        <input
                          type="color"
                          value={variant.color || '#1AA7DE'}
                          onChange={(e) => {
                            const newCol = e.target.value;
                            setVariant((prev) => ({
                              ...prev,
                              color: newCol,
                              value: newCol,
                            }));
                          }}
                          className="w-11 h-[38px] rounded-xl border border-slate-200 p-1 cursor-pointer bg-white shadow-2xs hover:border-slate-300 transition-colors"
                        />
                        <span className="h-[38px] inline-flex items-center font-mono text-xs font-bold text-slate-700 uppercase px-3 bg-white rounded-xl border border-slate-200 shadow-2xs">
                          {variant.color || '#1AA7DE'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <FormField
                        label="Color Name"
                        placeholder="e.g. Navy Blue, Emerald, Crimson"
                        value={variant.color_name}
                        onChange={(e) => {
                          const newName = e.target.value;
                          setVariant((prev) => ({
                            ...prev,
                            color_name: newName,
                            label: newName,
                          }));
                        }}
                        containerClassName="mb-0"
                      />
                    </div>
                  </div>
                )}

                {/* Size Configuration */}
                {variant.variant_type === 'SIZE' && (
                  <div className="p-4 bg-slate-50/80 rounded-xl border border-slate-200/80 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <FormField
                        label="Size Label"
                        placeholder="e.g. Men's Size, Shoe Size"
                        value={variant.label}
                        onChange={(e) => {
                          const newLabel = e.target.value;
                          setVariant((prev) => ({
                            ...prev,
                            label: newLabel,
                          }));
                        }}
                        containerClassName="mb-0"
                      />
                    </div>
                    <div>
                      <FormField
                        label="Size Value"
                        placeholder="e.g. Small, M, XL, 42 EU"
                        value={variant.value}
                        onChange={(e) => {
                          const newVal = e.target.value;
                          setVariant((prev) => ({
                            ...prev,
                            value: newVal,
                          }));
                        }}
                        containerClassName="mb-0"
                      />
                    </div>
                  </div>
                )}

                {/* Live Variant Preview */}
                {variant.variant_type && (
                  <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Configured Variant:
                      </span>
                      {variant.variant_type === 'COLOR' ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="w-5 h-5 rounded-md border border-slate-300 shadow-2xs inline-block"
                            style={{ backgroundColor: variant.color || '#1AA7DE' }}
                          />
                          <span className="text-xs font-bold text-slate-800">
                            {variant.color_name || variant.label || 'Color'}
                          </span>
                          <span className="font-mono text-[11px] text-slate-400">
                            ({variant.color || '#1AA7DE'})
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs font-bold text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg">
                          {variant.label ? `${variant.label}: ` : ''}{variant.value || 'Standard'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* 5. Product Highlights */}
            <SectionCard
              id="highlights-card"
              title={language_data?.[authState.lang]?.common_highlights || 'Product Highlights'}
              subtitle="Bullet points displayed prominently on the buyer product page"
              icon={<Tag className="w-5 h-5" />}
            >
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentHighlight}
                    onChange={(e) => {
                      setCurrentHighlight(e.target.value);
                      setHighlightError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddHighlight();
                      }
                    }}
                    placeholder="e.g. 100% Breathable Organic Cotton, Machine Washable..."
                    className="flex-1 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 text-xs sm:text-sm font-medium px-3.5 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]"
                  />
                  <button
                    type="button"
                    onClick={handleAddHighlight}
                    disabled={!currentHighlight.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-40"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add</span>
                  </button>
                </div>

                {highlightError && (
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600" role="alert">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{highlightError}</span>
                  </div>
                )}

                {highlightsArray.length > 0 && (
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
                    {highlightsArray.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 hover:bg-slate-50/60 transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="text-xs sm:text-sm font-semibold text-slate-800">
                            {highlight}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveHighlight(index)}
                          className="w-7 h-7 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors"
                          title="Remove highlight"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </SectionCard>

            {/* 6. Specifications & Services */}
            <SectionCard
              id="specifications-card"
              title="Specifications & Services"
              subtitle="Technical key-value attributes and warranty or service details"
              icon={<Shield className="w-5 h-5" />}
            >
              <div className="space-y-6">

                {/* Specifications Section */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Technical Specifications
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Key (e.g. Material)"
                      value={specKey}
                      onChange={(e) => {
                        setSpecKey(e.target.value);
                        setSpecError('');
                      }}
                      className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 text-xs sm:text-sm font-medium px-3.5 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]"
                    />
                    <input
                      type="text"
                      placeholder="Value (e.g. 100% Cotton)"
                      value={specValue}
                      onChange={(e) => {
                        setSpecValue(e.target.value);
                        setSpecError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSpecification();
                        }
                      }}
                      className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 text-xs sm:text-sm font-medium px-3.5 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]"
                    />
                    <button
                      type="button"
                      onClick={handleAddSpecification}
                      disabled={!specKey.trim() || !specValue.trim()}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-40"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add</span>
                    </button>
                  </div>

                  {specError && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 mb-2" role="alert">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{specError}</span>
                    </div>
                  )}

                  {specificationsArray.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {specificationsArray.map((spec, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-800 font-semibold text-xs border border-slate-200 shadow-2xs"
                        >
                          <span className="text-slate-500 font-normal">{spec.key}:</span>
                          <span>{spec.value}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSpecification(index)}
                            className="text-slate-400 hover:text-rose-600 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Services Section */}
                <div className="pt-4 border-t border-slate-100">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Services & Warranties
                  </h3>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="e.g. 1 Year Official Manufacturer Warranty, 7 Days Easy Return"
                      value={currentService}
                      onChange={(e) => {
                        setCurrentService(e.target.value);
                        setServiceError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddService();
                        }
                      }}
                      className="flex-1 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 text-xs sm:text-sm font-medium px-3.5 py-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]"
                    />
                    <button
                      type="button"
                      onClick={handleAddService}
                      disabled={!currentService.trim()}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-all disabled:opacity-40"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add</span>
                    </button>
                  </div>

                  {serviceError && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 mb-2" role="alert">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{serviceError}</span>
                    </div>
                  )}

                  {servicesArray.length > 0 && (
                    <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
                      {servicesArray.map((service, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 hover:bg-slate-50/60 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <Shield className="w-4 h-4 text-[#1AA7DE] shrink-0" />
                            <span className="text-xs sm:text-sm font-semibold text-slate-800">
                              {service}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveService(index)}
                            className="w-7 h-7 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors"
                            title="Remove service"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>
          </div>

          {/* RIGHT COLUMN: Sidebar (4 cols, sticky) */}
          <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-20">

            {/* 2. Product Categorization & Brand Card */}
            <SectionCard
              id="organization-card"
              title="Categorization"
              subtitle="Organize item by brand and cascading category levels"
              icon={<Layers className="w-5 h-5" />}
            >
              {/* Brand */}
              <Controller
                name="brand_id"
                control={control}
                render={({ field }) => (
                  <SelectField
                    id="brand_id"
                    label={`${language_data?.[authState.lang]?.common_brand || 'Brand'}`}
                    required
                    placeholder="Select Brand"
                    options={brands}
                    value={field.value}
                    isLoading={loadingBrands}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.brand_id?.message}
                  />
                )}
              />

              {/* Category Level 1 */}
              <Controller
                name="category_id"
                control={control}
                render={({ field }) => (
                  <SelectField
                    id="category_id"
                    label={`${language_data?.[authState.lang]?.common_category_level || 'Category Level'} 1`}
                    required
                    placeholder="Select Primary Category"
                    options={categories}
                    value={field.value}
                    isLoading={loadingCategories}
                    onChange={(val) => handleCategoryChange(val)}
                    onBlur={field.onBlur}
                    error={errors.category_id?.message}
                  />
                )}
              />

              {/* Category Level 2 */}
              <Controller
                name="subcategory_id"
                control={control}
                render={({ field }) => (
                  <SelectField
                    id="subcategory_id"
                    label={`${language_data?.[authState.lang]?.common_category_level || 'Category Level'} 2`}
                    required
                    placeholder="Select Subcategory"
                    options={subcategories}
                    value={field.value}
                    isLoading={loadingSubCategories}
                    isDisabled={!selectedCategoryId}
                    onChange={(val) => handleSubCategoryChange(val)}
                    onBlur={field.onBlur}
                    error={errors.subcategory_id?.message}
                    helperText={!selectedCategoryId ? 'Select Level 1 category first' : undefined}
                  />
                )}
              />

              {/* Category Level 3 */}
              <Controller
                name="sub_subcategory_id"
                control={control}
                render={({ field }) => (
                  <SelectField
                    id="sub_subcategory_id"
                    label={`${language_data?.[authState.lang]?.common_category_level || 'Category Level'} 3 (Optional)`}
                    placeholder="Select Sub-subcategory"
                    options={subSubcategories}
                    value={field.value || ''}
                    isLoading={loadingSubSubCategories}
                    isDisabled={!selectedSubcategoryId}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.sub_subcategory_id?.message}
                    helperText={!selectedSubcategoryId ? 'Select Level 2 subcategory first' : undefined}
                  />
                )}
              />
            </SectionCard>

            {/* 3. Form Action Card */}
            <div className="p-4 bg-white rounded-xl border border-slate-100 shadow-xs space-y-2.5">
              <Button
                type="submit"
                variant="default"
                disabled={isSubmitting}
                className="w-full gap-2"
              >
                {isSubmitting ? (
                  <Spinner color="text-white" />
                ) : (
                  <>
                    {mode === 'clone' ? (
                      <Copy className="w-4 h-4" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>
                      {mode === 'add'
                        ? language_data?.[authState.lang]?.common_save || 'Publish Product'
                        : mode === 'clone'
                          ? language_data?.[authState.lang]?.common_clone || 'Clone Product'
                          : language_data?.[authState.lang]?.common_edit || 'Save Changes'}
                    </span>
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="w-full"
              >
                {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
